import type { Ref } from 'vue'
import { computed, createSharedComposable, onMounted, ref, useCookie, useDayjs, useNameserviceClient, useNuxtApp, useSessionExpiry, useUserInfo, watch } from '#imports'
import type { UseFreshUsernameReturn, UseUsernameReturn } from '../../types'
import { isPrefetching } from '../../utils/prefetch'

declare module '#app' {
    interface NuxtApp {
        /** The render's one username instance; the server creates the app per request. */
        _username?: UseUsernameReturn
    }
}

interface UsernameCookie {
    playerId: string
    name: string
}

/** The last non-empty username the nameservice gave, keyed to user/info's `playerId`. */
export function useUsernameCookie(): Ref<UsernameCookie | null | undefined> {
    const dayjs = useDayjs()

    return useCookie<UsernameCookie | null | undefined>('username', {
        secure: true,
        sameSite: 'lax',
        maxAge: dayjs.duration({ years: 1 }).asSeconds(),
    })
}

const _useUsername = (): UseUsernameReturn => {
    const usernameCookie = useUsernameCookie()
    const sessionExpiry = useSessionExpiry()
    const { data: user, isLoading: isUserLoading, userJustLoggedIn, userJustSignedUp } = useUserInfo()

    // A login can follow an expired session, whose cookie may hold a name since changed: right after one
    // the nameservice answers, and `remember` overwrites the cookie. The flags clear on the next route change.
    const cookie = computed(() => userJustLoggedIn.value || userJustSignedUp.value ? undefined : usernameCookie.value)
    const playerId = computed(() => user.value?.playerId)
    const cachedName = computed(() => cookie.value?.name && cookie.value.playerId === playerId.value ? cookie.value.name : null)
    // A cookie waits for user/info to tell whose it is; without one the nameservice starts at once.
    const awaitsPlayer = computed(() => !!cookie.value && !user.value && isUserLoading.value)

    const isEnabled = () => !!sessionExpiry.value && !cachedName.value && !awaitsPlayer.value
    const query = useNameserviceClient().useGetUsername({ enabled: isEnabled })

    function remember() {
        const name = query.data.value?.name

        // Never an empty name: the next render would ask the nameservice anyway.
        if (name && playerId.value) usernameCookie.value = { playerId: playerId.value, name }
    }

    // Watchers don't run during SSR, where both queries resolve once before the render.
    if (import.meta.server) void Promise.all([query, useUserInfo()]).then(remember, () => {})
    else watch([() => query.data.value, playerId], remember, { immediate: true })

    // The server gives the nameservice a second, so a slow answer hydrates as a failure that nothing
    // refetches: the browser asks once more after hydration. Loading from the start, so chat waits for it.
    const nuxtApp = useNuxtApp()
    const isRetrying = ref(import.meta.client && nuxtApp.isHydrating && !!query.error.value && isEnabled())

    // Not returned to the hook: Nuxt runs its hooks in series, and the retry must not hold the others up.
    if (isRetrying.value) nuxtApp.hooks.hookOnce('app:suspense:resolve', () => void query.refresh().finally(() => isRetrying.value = false))

    // A fetched answer wins over the cookie, so a refresh reaches past it.
    const username = computed(() => query.data.value ? query.data.value.name : cachedName.value)
    const isLoading = computed(() => isRetrying.value || awaitsPlayer.value || query.isLoading.value)

    return query.enrich({ username, isLoading })
}

/** One instance per render, like `useUserInfo`: its consumers share one nameservice call. */
function useUsernamePerRequest(): UseUsernameReturn {
    const nuxtApp = useNuxtApp()

    if (isPrefetching(nuxtApp)) return _useUsername()

    return nuxtApp._username ??= _useUsername()
}

/**
 * The player's username, from the `username` cookie when it belongs to the logged-in player and
 * otherwise from the nameservice. `username` is `null` for a player without one, a signed-out
 * visitor, a 401 and a 404. Refresh it after the player changes their username.
 */
export const useUsername: () => UseUsernameReturn = import.meta.client ? createSharedComposable(_useUsername) : useUsernamePerRequest

/**
 * For the profile forms: the username fetched from the nameservice on mount, never the cookie's,
 * so a stale cached name (e.g. one support changed) is never sent back in a save.
 */
export function useFreshUsername(): UseFreshUsernameReturn {
    const query = useUsername()
    const isLoading = ref(true)
    const username = computed(() => query.data.value?.name ?? null)
    const hasFailed = computed(() => !!query.error.value)

    async function refresh() {
        try {
            await query.refresh()
        }
        finally {
            isLoading.value = false
        }
    }

    onMounted(refresh)

    return query.bind({ username, isLoading, hasFailed, refresh })
}
